import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, MessageCircle, ArrowRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Footer } from "@/components/Footer";

export default function Login() {
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const otpRef = useRef(null);
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    if (seconds <= 0) return;
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds]);

  const sendOtp = async () => {
    const digits = phone.replace(/\D/g, "").slice(-10);
    if (digits.length !== 10) { toast.error("Enter a valid 10-digit mobile number"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/send-otp", { phone: digits });
      setStep("otp");
      setSeconds(30);
      toast.success("OTP sent on WhatsApp", { description: `Demo code: ${data.dev_otp}` });
      setTimeout(() => otpRef.current?.focus(), 200);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send OTP");
    } finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setLoading(true);
    try {
      const digits = phone.replace(/\D/g, "").slice(-10);
      const { data } = await api.post("/auth/verify-otp", { phone: digits, otp });
      login(data.token, data.user);
      toast.success("Welcome to Webtu!");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verification failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden"
      style={{ background: "radial-gradient(ellipse at top, rgba(201,162,75,0.13), transparent 60%), #0A0A0A" }}>
      <div className="flex-1 flex items-center justify-center px-5 py-10">
        <div className="w-full max-w-md wt-fade">
          <div className="flex justify-center mb-8"><Logo size={52} /></div>

          <div className="bg-[#161616] border border-[#27272A] rounded-3xl p-7 sm:p-9 shadow-2xl">
            {step === "phone" ? (
              <>
                <h1 className="font-display text-3xl font-bold mb-2">Get your website</h1>
                <p className="text-zinc-400 text-sm mb-7">Login with your WhatsApp number. No passwords, no hassle.</p>

                <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Mobile Number</label>
                <div className="mt-2 flex items-center bg-[#0A0A0A] border border-[#27272A] rounded-xl focus-within:border-[#C9A24B] transition-all overflow-hidden">
                  <span className="px-4 py-3.5 text-zinc-300 border-r border-[#27272A] text-sm font-medium">+91</span>
                  <input
                    data-testid="auth-phone-input"
                    inputMode="numeric"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    onKeyDown={(e) => e.key === "Enter" && sendOtp()}
                    placeholder="98765 43210"
                    className="flex-1 bg-transparent px-4 py-3.5 outline-none text-[#F5F5F7] placeholder:text-zinc-600"
                  />
                </div>

                <button
                  data-testid="auth-send-otp-btn"
                  onClick={sendOtp} disabled={loading}
                  className="mt-6 w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#22bf5b] text-[#04120a] font-bold py-3.5 rounded-xl transition-all active:scale-[0.98] disabled:opacity-60"
                >
                  {loading ? <Loader2 className="w-5 h-5 wt-spin" /> : <MessageCircle className="w-5 h-5" />}
                  Send OTP on WhatsApp
                </button>
              </>
            ) : (
              <>
                <h1 className="font-display text-3xl font-bold mb-2">Enter OTP</h1>
                <p className="text-zinc-400 text-sm mb-7">
                  We sent a 6-digit code to <span className="text-[#E5C378] font-semibold">+91 {phone}</span>.
                </p>

                <input
                  data-testid="auth-otp-input"
                  ref={otpRef}
                  inputMode="numeric"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  onKeyDown={(e) => e.key === "Enter" && verifyOtp()}
                  placeholder="• • • • • •"
                  className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#C9A24B] rounded-xl px-4 py-4 text-center text-2xl tracking-[0.5em] outline-none text-[#F5F5F7] placeholder:text-zinc-700"
                />

                <button
                  data-testid="auth-verify-otp-btn"
                  onClick={verifyOtp} disabled={loading}
                  className="mt-6 w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#C9A24B] to-[#E5C378] text-[#0A0A0A] font-bold py-3.5 rounded-xl transition-all active:scale-[0.98] disabled:opacity-60 wt-pulse"
                >
                  {loading ? <Loader2 className="w-5 h-5 wt-spin" /> : <>Verify & Continue <ArrowRight className="w-5 h-5" /></>}
                </button>

                <div className="mt-4 flex items-center justify-between text-sm">
                  <button onClick={() => setStep("phone")} className="text-zinc-400 hover:text-white">Change number</button>
                  <button onClick={sendOtp} disabled={seconds > 0}
                    className="text-[#E5C378] disabled:text-zinc-600">
                    {seconds > 0 ? `Resend in ${seconds}s` : "Resend OTP"}
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="mt-6 flex items-center justify-center gap-5 text-xs text-zinc-500">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#C9A24B]" /> 100% Secure</span>
            <span>A�</span>
            <span>Trusted by Indian businesses</span>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
